import React from 'react'
import classnames from 'classnames/bind'
import { subMonths, startOfDay, endOfDay } from 'date-fns'
import { UnixTimestamp, DateInterval } from '@caudena/core/types'
import useHttp from '@caudena/core/useHttp'
import buildUrl from '@caudena/core/utils/buildUrl'
import {
  normalizeSeriesDate,
  groupSeriesByWeek,
  groupSeriesByMonth,
  getSeriesExtremum,
} from '@caudena/core/ui-kit'
import Portlet from '@caudena/core/Portlet'
import { Container } from '@caudena/core/ui-kit'
import { Typography } from '@caudena/core/ui-kit'
import { Stub } from '@caudena/core/ui-kit'
import { RowDeprecated } from '@caudena/core/ui-kit'
import { MagicGrid } from '@caudena/core/ui-kit'
import { ZoomRangeButtonSelect } from '@caudena/core/ui-kit'
import { CalendarInput } from '@caudena/core/ui-kit'

import {
  TransfersVolumeData,
  TransfersVolumeApiParams,
} from '../../../types/Dashboard'
import TransfersFlow from '../../Chart/transfersFlow'
import VolumeDonut from '../../Chart/volumeDonut'
import { GroupByInput, GroupByValue } from '@caudena/core/ui-kit'
import AssetSuggest from '../../Assets/AssetsSuggest'

import styles from './index.scss'
import { getConfig } from '@caudena/core/useConfig'
import { useTimezone } from '@compliance/hooks'
import { fromZonedTime } from 'date-fns-tz'
import { CoinType } from '@compliance/types/coin'
const cx = classnames.bind(styles)

const getDefaultZoomValue = (timeZone = 'UTC') => {
  return {
    start: startOfDay(fromZonedTime(subMonths(new Date(), 6), timeZone)),
    end: endOfDay(fromZonedTime(new Date(), timeZone)),
  }
}

const TransfersVolumeBlock: React.FC = () => {
  const [assetId, setAssetId] = React.useState<string | null>(null)
  const [currency, setCurrency] = React.useState<CoinType | null>(null)

  const handleChageAsset = (id, currency) => {
    setAssetId(id)
    setCurrency(currency)
  }

  const apiParams: TransfersVolumeApiParams = {
    asset_id: assetId ? Number(assetId) : undefined,
    currency: currency ?? undefined,
  }

  const {
    data: source,
    isLoading,
    isFirstLoading,
  } = useHttp<TransfersVolumeData>(
    buildUrl`${getConfig()?.COMPLIANCE_API}/api/dash/trxs?${apiParams}`
  )
  const timeZone = useTimezone()

  const data = React.useMemo(() => {
    const normalized = normalizeSeriesDate<[UnixTimestamp, number]>(
      source?.flow ?? []
    )
    const [min, max] = getSeriesExtremum(normalized)

    return {
      min,
      max,
      inflow: normalized.filter(([x, y]) => y >= 0),
      outflow: normalized.filter(([x, y]) => y < 0),
    }
  }, [source])

  const handleChangeZoom = (zoomInterval: DateInterval) => {
    setZoomValue({
      start: zoomInterval.start,
      end: zoomInterval.end,
    })
    setCalendar([zoomInterval.start, zoomInterval.end])
  }

  const handleChangeCalendar = ([from, to]: [Date, Date]) => {
    const interval = {
      start: from,
      end: to,
    }

    setZoomValue(interval)
    setCalendar([from, to])
  }

  const volumeData = React.useMemo(
    () => ({
      in: source?.in,
      out: source?.out,
      volume: source?.volume,
    }),
    [source]
  )

  // атеншн! группировать inflow и outflow важно раздельно, иначе они взаимноуничтожатся
  const groupedData = React.useMemo(
    () => ({
      day: { inflow: data.inflow, outflow: data.outflow },
      week: {
        inflow: groupSeriesByWeek(data.inflow),
        outflow: groupSeriesByWeek(data.outflow),
      },
      month: {
        inflow: groupSeriesByMonth(data.inflow),
        outflow: groupSeriesByMonth(data.outflow),
      },
    }),
    [data.inflow, data.outflow]
  )

  const [groupBy, setGroupBy] = React.useState<GroupByValue>('week')

  function handleChangeGroupBy(groupByValue) {
    setGroupBy(groupByValue)
  }

  const [zoom, setZoomValue] = React.useState(getDefaultZoomValue(timeZone))
  const [calendar, setCalendar] = React.useState<[Date, Date] | []>([
    zoom.start,
    zoom.end,
  ])

  const handleChangeChartZoom = (chartInterval: DateInterval) => {
    setZoomValue(chartInterval)
    setCalendar([chartInterval.start, chartInterval.end])
  }

  return (
    <Portlet variant="card">
      {{
        title: (
          <Typography variant="heading3" color="grey1">
            Transfers volume
          </Typography>
        ),
        body: isFirstLoading ? (
          <Stub className={cx('TransfersVolumePortletStub')} isActive={true} />
        ) : (
          <MagicGrid>
            <Container gap={[2, 2, 0, 2]}>
              <RowDeprecated align="between" wrap>
                <ZoomRangeButtonSelect
                  min={data.min}
                  max={data.max}
                  value={zoom}
                  onChange={handleChangeZoom}
                  className={cx('Zoom')}
                />
                <CalendarInput
                  variant="outline"
                  placeholder="Select period"
                  value={calendar}
                  onChange={handleChangeCalendar}
                />
                <AssetSuggest
                  value={assetId ?? undefined}
                  onChange={handleChageAsset}
                />
                <GroupByInput
                  variant="outline"
                  value={groupBy}
                  onChange={handleChangeGroupBy}
                />
              </RowDeprecated>
            </Container>
            <MagicGrid cols={[9, 3]} className={cx('Charts')}>
              <TransfersFlow
                loading={isLoading}
                type="volume"
                data={groupedData[groupBy]}
                zoom={zoom}
                onZoomChange={handleChangeChartZoom}
              />
              <VolumeDonut loading={isLoading} data={volumeData} />
            </MagicGrid>
          </MagicGrid>
        ),
      }}
    </Portlet>
  )
}

export default TransfersVolumeBlock
